import {
  InteractMenuIcon,
  MediaDiscordIcon,
  MediaHelpIcon,
  MediaMediumIcon,
  MediaPrivacyIcon,
  MediaServiceIcon,
  MediaTelegramIcon,
  MediaWebsiteIcon,
  MediaXIcon,
  ViewInfoIcon,
} from '@/client/assets';
import { SwitchLocale } from '@/client/components/switch-locale';
import { SwitchTheme } from '@/client/components/switch-theme';
import { Button } from '@/client/components/ui/button';
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from '@/client/components/ui/dropdown-menu';
import {
  DISCORD_LINK,
  HELP_LINK,
  MEDIUM_LINK,
  PRIVACY_LINK,
  TELEGRAM_LINK,
  TERMS_OF_USE_LINK,
  TWITTER_LINK,
  WEBSITE_LINK,
} from '@/client/confs/app';
import { cn } from '@/client/utils';
import { DropdownMenuContent } from '@radix-ui/react-dropdown-menu';
import { useTranslation } from 'react-i18next';
import useMedia from 'react-use/lib/useMedia';

const otherList = [
  {
    name: 'telegram',
    href: TELEGRAM_LINK,
    icon: MediaTelegramIcon,
  },
  {
    name: 'developer',
    href: HELP_LINK,
    icon: ViewInfoIcon,
  },
  {
    name: 'website',
    href: WEBSITE_LINK,
    icon: MediaWebsiteIcon,
  },
  {
    name: 'term-of-use',
    href: TERMS_OF_USE_LINK,
    icon: MediaServiceIcon,
  },
  {
    name: 'privacy-policy',
    href: PRIVACY_LINK,
    icon: MediaPrivacyIcon,
  },
];

export const TheMarketFooter = () => {
  const { t } = useTranslation();
  const isDesktop = useMedia('(min-width: 768px)');
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  return (
    <>
      {isDesktop && (
        <footer className="w-full h-10 border-t border-border bg-background pos-fixed bottom-0 flex items-center justify-between gap-3 z-10">
          <div className="w-full px-4 md:px-6 flex items-center justify-between size-full mx-auto max-w-[1400px] gap-4">
            {/* Left: Language + Theme + Status */}
            <div className="flex items-center justify-start gap-4 flex-1">
              {/* Language Selector */}
              <SwitchLocale variant="text" />

              {/* Theme Toggle */}
              <SwitchTheme />

            </div>

            {/* Right: Back to Top + Docs + Support + Social */}
            <div className="flex items-center justify-end gap-4">
              <Button
                variant="link"
                size="sm"
                onClick={scrollToTop}
                className="text-xs leading-none font-medium text-foreground/60 hover:text-foreground transition-colors p-0 h-auto"
              >
                {t('back-to-top')}
              </Button>

              {HELP_LINK && (
                <a
                  href={HELP_LINK}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 text-xs text-foreground/60 hover:text-foreground transition-colors"
                >
                  <MediaHelpIcon className="size-4 fill-current" />
                  <span>{t('support')}</span>
                </a>
              )}

              {/* Social Links */}
              <div className="flex items-center gap-2 border-l border-foreground/10 pl-4">
                {DISCORD_LINK && (
                  <a
                    href={DISCORD_LINK}
                    aria-label="Discord"
                    target="_blank"
                    rel="noreferrer"
                    className="text-foreground/50 hover:text-foreground transition-colors"
                  >
                    <MediaDiscordIcon className="size-4 fill-current" />
                  </a>
                )}
                {TWITTER_LINK && (
                  <a
                    href={TWITTER_LINK}
                    aria-label="X (Twitter)"
                    target="_blank"
                    rel="noreferrer"
                    className="text-foreground/50 hover:text-foreground transition-colors"
                  >
                    <MediaXIcon className="size-4 fill-current" />
                  </a>
                )}
                {MEDIUM_LINK && (
                  <a
                    href={MEDIUM_LINK}
                    aria-label="Medium"
                    target="_blank"
                    rel="noreferrer"
                    className="text-foreground/50 hover:text-foreground transition-colors"
                  >
                    <MediaMediumIcon className="size-4 fill-current" />
                  </a>
                )}

                {/* More Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="w-auto h-auto p-0 text-foreground/50 hover:text-foreground cursor-pointer transition-colors"
                    >
                      <InteractMenuIcon className="size-4 fill-current" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="top"
                    align="end"
                    alignOffset={0}
                    className="mb-3 py-2 px-2 w-[180px] rounded-3 bg-card border border-foreground/6 shadow-lg"
                  >
                    {otherList.filter(item => item.href).map(item => (
                      <DropdownMenuItem key={item.name} className="px-2 py-2 rounded-2 cursor-pointer">
                        <a
                          href={item.href}
                          aria-label={item.name}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center w-full text-foreground/70 text-sm font-medium hover:text-foreground space-x-2.5 transition-colors"
                        >
                          <item.icon aria-hidden="true" className="size-4 fill-current" />
                          <span className="flex-1">{t(item.name)}</span>
                        </a>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </footer>
      )}
      {/* Mobile: bottom spacing above the fixed nav */}
      {!isDesktop && <div className="pb-20" />}
    </>
  );
};
